import { defineStore, storeToRefs } from 'pinia';
import type { RemovableRef } from '@vueuse/core';
import { useStorage } from '@vueuse/core';
import { useSum } from '@vueuse/math';
import type { identifier } from '@core/models/base';
import type { SubjectWithCartPackages_CartServices } from '@apps/cart/services/cart.service';
import { deleteCart, fetchCartDetails, groupCartDetailsBySubject } from '@apps/cart/services/cart.service';
import type { Cartv2DetailsDisplayModel } from '@shared/models/carts/carts';
import type { CartPackageUpdateModel, Cartv2PackageDetailsDisplayModel } from '@shared/models/carts/cartPackages';
import { deleteCartPackage, patchCartPackage } from '@apps/customer-package-builder/services/cartPackage.service';
import { consumerTextNodeFillOptions } from '@apps/customer-package-builder/services/cartPackageNode.service';
import {
  deleteCartOrderService,
  setupRetouchData,
  setupPersonalizationData,
} from '@apps/cart/services/cartOrderServices.service';
import type { JobOrderServiceDisplayModel } from '@shared/models/jobs/jobOrderServices';
import { fetchJobOrderServices } from '@apps/cart/services/jobOrderServices.service';
import { nameOnWalletProducts, nameOnAllPrintProducts } from '@shared/models/jobs/jobOrderServices';
import { useCheckoutStore } from '@apps/checkout/stores/checkout.store';
import { useCurrencyArithmetic } from '@shared/composables/useCurrencyArithmetic';
import { usePackageAddonService } from '@apps/job-editor/services/packageAddon.service';
import type {
  PersonalizationCartItemClientDisplayModel,
  RetouchCartItemClientDisplayModel,
} from '@shared/models/carts/cartOrderSevices';
import type {
  AvailableImageServiceTypeDisplayModel,
  CartImageServiceSelectionDisplayModel,
} from '@shared/models/images/imageServices';
import {
  fetchAvailableImageServices,
  fetchCartImageServiceSelections,
} from '@apps/cart/services/cartImageServiceSelections.service';
import { useCustomerStore } from './customer.store';
import { useCartPackageStore } from '@apps/customer-package-builder/stores/cartPackage.store';
import { JobOrderMethod } from '@shared/models/subjects/accessCode';

export interface UpsertServices {
  retouches?: RetouchCartItemClientDisplayModel[];
  personalizations?: PersonalizationCartItemClientDisplayModel[];
}
export interface CartState {
  loading: boolean;
  removingCartPackage: boolean;
  persistedJobId: RemovableRef<identifier | null>;
  persistedPackageLength: RemovableRef<number>;
  persistedOrderMethod: RemovableRef<JobOrderMethod | null>;

  studioId: identifier;
  cartDetails?: Cartv2DetailsDisplayModel;
  jobOrderServices?: JobOrderServiceDisplayModel[];
  upsertServices: UpsertServices;
  availableImageServices?: AvailableImageServiceTypeDisplayModel[];
  cartImageServiceSelections?: CartImageServiceSelectionDisplayModel[];
}
const { isStandardPackage, hasStandardPackage, validatePackage } = usePackageAddonService();
const { multiply, subtract, add } = useCurrencyArithmetic();
export const useCartStore = defineStore('cartStore', {
  state: (): CartState => ({
    loading: false,
    removingCartPackage: false,
    persistedJobId: useStorage<identifier>('cart-jobId', null),
    persistedPackageLength: useStorage<number>('cart-package-length', 0),
    persistedOrderMethod: useStorage<JobOrderMethod>('cart-order-method', null),
    studioId: 0,
    cartDetails: undefined,
    jobOrderServices: undefined,
    upsertServices: { retouches: undefined, personalizations: undefined },
    availableImageServices: undefined,
    cartImageServiceSelections: undefined,
  }),
  getters: {
    apiRootUrl: (state) => `api/customer/jobs/${state.persistedJobId}/carts`,
    subjectsWithPackages: (state): SubjectWithCartPackages_CartServices[] => {
      const customerStore = useCustomerStore();
      const { hasImportedData } = storeToRefs(customerStore);
      return groupCartDetailsBySubject(
        state.cartDetails,
        hasImportedData.value,
        consumerTextNodeFillOptions(!!customerStore.job?.isV3),
      );
    },

    itemCount: (state) =>
      useSum(
        state.cartDetails?.cartPackages?.length ?? 0,
        state.cartDetails?.cartOrderServicesDetails?.cartOrderServices?.length ?? 0,
      ).value,
    addonPackages: (state): Cartv2PackageDetailsDisplayModel[] | undefined => {
      return state.cartDetails?.cartPackages.filter((cp) => !isStandardPackage(cp));
    },
    cartPackages: (state): Cartv2PackageDetailsDisplayModel[] | undefined => {
      return state.cartDetails?.cartPackages;
    },
    hasAddonPackages(): boolean {
      return !!this.addonPackages?.length;
    },
    hasStandardPackage: (state) => {
      return hasStandardPackage(state.cartDetails?.cartPackages);
    },
    /**
     * Find and return the job's retouch service, if any
     */
    retouchServices: (state) => state.jobOrderServices?.find((j) => j.allowRetouch),
    /**
     * Job has any service
     */
    hasServices(): boolean {
      return this.hasHostedServices || this.hasPrePayServices;
    },
    /**
     * Job has any hosted service, for now this is only Image level Retouching
     */
    hasHostedServices: (state): boolean => {
      return state.jobOrderServices?.some((j) => j.imageLevelService) ?? false;
    },
    /**
     * Job has V3 image services available (RSPO)
     */
    hasV3ImageServices: (state): boolean => {
      return (state.availableImageServices?.length ?? 0) > 0;
    },
    /**
     * Total price of V3 image service selections in the cart
     */
    v3ImageServiceTotal: (state): number => {
      const prices = state.cartImageServiceSelections?.map((s) => s.price) ?? [0];
      return add(...prices);
    },
    /**
     * Job has any prepay service
     */
    hasPrePayServices(state): boolean {
      const cartPackageNames =
        state.cartDetails?.cartPackages.flatMap((cp) => cp.cartPackageItems.flatMap((cpi) => cpi.name)) || [];

      const hasServiceableWalletItems = cartPackageNames.some((p) => nameOnWalletProducts.includes(p));
      const hasServiceablePrintItems = cartPackageNames.some((p) => nameOnAllPrintProducts.includes(p));

      return (
        this.hasRetouchService ||
        (this.hasNameOnAllPrintService && hasServiceablePrintItems) ||
        (this.hasNameOnWalletService && hasServiceableWalletItems)
      );
    },
    /**
     * [PREPAY] Job has Name on All Prints service
     */
    hasNameOnAllPrintService: (state) => {
      return state.jobOrderServices?.some((j) => j.allowNameOnAllPrints) ?? false;
    },
    /**
     * [PREPAY] Job has Name on Wallet service
     */
    hasNameOnWalletService: (state) => {
      return state.jobOrderServices?.some((j) => j.allowNameOnWallets) ?? false;
    },
    /**
     * [PREPAY] Job has Retouch service
     */
    hasRetouchService: (state) => {
      return state.jobOrderServices?.some((j) => j.allowRetouch) ?? false;
    },
    /**
     * Cart total
     */
    total: (state): number => {
      const packagePrices = state.cartDetails?.cartPackages?.map((i) => multiply(i.price, i.quantity)) ?? [0];
      // Charge distinct backgrounds only once each (not per package quantity)
      const uniqueBackgrounds = new Map<number, number>();
      state.cartDetails?.cartPackages?.forEach((i) => {
        if (i.background?.id && i.background.cost > 0) {
          uniqueBackgrounds.set(i.background.id, i.background.cost);
        }
      });
      const backgroundPrices = [...uniqueBackgrounds.values()];
      const servicePrices = state.cartDetails?.cartOrderServicesDetails?.cartOrderServices?.map((i) => i.price) ?? [0];
      return add(...packagePrices, ...backgroundPrices, ...servicePrices);
    },
    /**
     * [PREPAY] Total for a prepay cart before services have been saved
     */
    prepayCartTotal: (state): number => {
      const retouchPrices = state.upsertServices.retouches?.filter((p) => p.enabled).map((p) => p.price) ?? [0];
      const personalizationPrices = state.upsertServices.personalizations
        ?.filter((p) => p.enabled)
        .map((p) => p.price) ?? [0];
      const packagePrices = state.cartDetails?.cartPackages.map((p) => multiply(p.price, p.quantity)) ?? [0];
      // Charge distinct backgrounds only once each (not per package quantity)
      const uniqueBackgrounds = new Map<number, number>();
      state.cartDetails?.cartPackages?.forEach((p) => {
        if (p.background?.id && p.background.cost > 0) {
          uniqueBackgrounds.set(p.background.id, p.background.cost);
        }
      });
      const backgroundPrices = [...uniqueBackgrounds.values()];

      return add(...retouchPrices, ...personalizationPrices, ...packagePrices, ...backgroundPrices);
    },
  },
  actions: {
    async initialize(studioId: identifier | null, jobId?: identifier, force = false) {
      const promises = [];
      if (jobId != null && (force || this.cartDetails?.jobId != jobId)) {
        promises.push(this.loadDetails(jobId));
      }
      if (studioId != null && jobId != null && (force || this.jobOrderServices?.some((j) => j.jobId != jobId))) {
        promises.push(this.loadJobOrderServices(studioId, jobId));
      }
      await Promise.all(promises);
    },

    async initializeUpsertService() {
      if (this.cartDetails && this.hasRetouchService) {
        this.upsertServices.retouches = setupRetouchData(
          this.cartDetails?.cartOrderServicesDetails,
          this.cartDetails?.subjects || [],
          (this.cartDetails as Cartv2DetailsDisplayModel).id,
          this.retouchServices as JobOrderServiceDisplayModel,
        );
      }

      if (this.cartDetails && (this.hasNameOnAllPrintService || this.hasNameOnWalletService)) {
        this.upsertServices.personalizations = setupPersonalizationData(
          this.cartDetails?.cartOrderServicesDetails,
          this.cartDetails?.subjects || [],
          (this.cartDetails as Cartv2DetailsDisplayModel).id,
          this.jobOrderServices as JobOrderServiceDisplayModel[],
        );
      }
    },

    async loadJobOrderServices(studioId: identifier, jobId: identifier) {
      this.studioId = studioId;
      this.jobOrderServices = await fetchJobOrderServices(studioId, jobId);
    },

    async loadAvailableImageServices(jobId: identifier) {
      this.availableImageServices = await fetchAvailableImageServices(jobId);
    },

    async loadCartImageServiceSelections(jobId: identifier) {
      this.cartImageServiceSelections = await fetchCartImageServiceSelections(jobId);
    },

    async setDetails(details: Cartv2DetailsDisplayModel) {
      this.cartDetails = details;
    },
    async loadDetails(jobId: identifier) {
      this.persistedJobId = jobId;
      this.cartDetails = await fetchCartDetails(jobId, this.persistedOrderMethod!);
      this.persistedPackageLength = this.cartDetails?.cartPackages?.length ?? 0;
      //TODO: Fix when fetch returns service result
      //const result = await fetchCartDetails(jobId);
      //if (isServiceSuccess(result)) this.cartDetails = result.data;
    },
    clear() {
      this.persistedJobId = null;
      this.persistedPackageLength = 0;
      this.persistedOrderMethod = null;
      this.studioId = 0;
    },
    emptyCart() {
      this.cartDetails = undefined;
      this.jobOrderServices = undefined;
      this.availableImageServices = undefined;
      this.cartImageServiceSelections = undefined;
      this.persistedPackageLength = 0;
      const { clear: clearCheckout } = useCheckoutStore();
      const { clear: clearCartPackage } = useCartPackageStore();
      clearCheckout();
      clearCartPackage();
    },

    async validateAddonPackages(validateStandard: boolean) {
      if (this.hasAddonPackages) {
        await Promise.all(
          this.addonPackages!.map(async (p) => {
            if (
              !validatePackage(
                p,
                multiply(p.price, p.quantity),
                this.total,
                validateStandard ? this.cartDetails?.cartPackages : undefined,
              )
            ) {
              this.removeCartPackage(p.id);
            }
          }),
        );
      }
    },

    /**
     * Update cart package quantity
     * @param id cartPackageId
     * @param model Update model
     */
    async updatePackage(id: identifier, model: CartPackageUpdateModel) {
      const match = this.cartDetails?.cartPackages.find((i) => i.id === id);
      if (match && model.quantity) {
        match.quantity = model.quantity;
        await patchCartPackage(this.persistedJobId as identifier, id, model);
        await this.validateAddonPackages(false);
      }
      this.persistedPackageLength = this.cartDetails?.cartPackages?.length ?? 0;
    },
    async removeService(id: identifier, orderServiceId: number) {
      if (!this.cartDetails) return;

      this.cartDetails.cartOrderServicesDetails.cartOrderServices.splice(
        this.cartDetails.cartOrderServicesDetails.cartOrderServices.findIndex(
          (i) => i.id == id && i.orderServiceId == orderServiceId,
        ),
        1,
      );

      this.cartDetails.cartOrderServicesDetails.imageLevel.splice(
        this.cartDetails.cartOrderServicesDetails.imageLevel.findIndex(
          (i) => i.id == id && i.orderServiceId == orderServiceId,
        ),
        1,
      );

      this.cartDetails.cartOrderServicesDetails.retouches.splice(
        this.cartDetails.cartOrderServicesDetails.retouches.findIndex(
          (i) => i.id == id && i.orderServiceId == orderServiceId,
        ),
        1,
      );

      this.cartDetails.cartOrderServicesDetails.personalizations.splice(
        this.cartDetails.cartOrderServicesDetails.personalizations.findIndex(
          (i) => i.id == id && i.orderServiceId == orderServiceId,
        ),
        1,
      );

      await deleteCartOrderService(this.persistedJobId as identifier, id);
      await this.validateAddonPackages(false);
    },

    /**
     * Checks if removing the package will also remove an addon package
     */
    checkIfUpdateRemovesAddonCartPackage(id: identifier, serviceId?: identifier, quantity?: number): boolean {
      if (this.hasAddonPackages) {
        if (serviceId) {
          return this.checkRemovingService(id, serviceId);
        } else if (quantity) {
          return this.checkUpdatingQuantity(id, quantity);
        }
        {
          return this.checkRemovingPackage(id);
        }
      }
      return false;
    },

    checkIfAddonCartIsInvalid(): boolean {
      if (this.hasAddonPackages) {
        return this.addonPackages!.some((p) => {
          return !validatePackage(p, multiply(p.price, p.quantity), this.total, this.cartDetails?.cartPackages);
        });
      }
      return false;
    },

    /**
     * Checks if updating the package will also remove an addon package
     * @param id CartPackageId
     * @param quantity New quantity
     */
    checkUpdatingQuantity(id: identifier, quantity: number) {
      const packageToRemove = this.cartDetails?.cartPackages?.find((p) => p.id === id);

      return this.addonPackages!.some((p) => {
        const oldPrice = multiply(packageToRemove!.price, packageToRemove!.quantity);
        const newPrice = multiply(packageToRemove!.price, quantity);
        const totalForValidation = add(subtract(this.total, oldPrice), newPrice);
        return !validatePackage(p, multiply(p.price, p.quantity), totalForValidation);
      });
    },

    /**
     * Checks if removing the package will also remove an addon package
     * @param id CartPackageId
     */
    checkRemovingPackage(id: identifier) {
      const filteredPackages = this.cartDetails?.cartPackages?.filter((p) => p.id !== id);
      const packageToRemove = this.cartDetails?.cartPackages?.find((p) => p.id === id);

      if (!hasStandardPackage(filteredPackages)) {
        //If package is last standard package
        return true;
      }

      const filteredAddonPackages = this.addonPackages!.filter((p) => p.id !== id);
      if (filteredAddonPackages.length === 0) {
        return false;
      }

      return filteredAddonPackages!.some(
        (p) =>
          !validatePackage(
            p,
            multiply(p.price, p.quantity),
            subtract(this.total, multiply(packageToRemove!.price, packageToRemove!.quantity)),
          ),
      );
    },

    /**
     * Checks if removing the service will also remove an addon package
     * @param id CartPackageId
     * @param orderServiceId orderServiceId
     */
    checkRemovingService(id: identifier, orderServiceId: number) {
      const serviceToRemove = this.cartDetails?.cartOrderServicesDetails.cartOrderServices.find(
        (p) => p.id === id && p.orderServiceId === orderServiceId,
      );

      return this.addonPackages!.some(
        (p) =>
          !validatePackage(
            p,
            multiply(p.price, p.quantity),
            subtract(this.total, multiply(serviceToRemove!.price, serviceToRemove!.quantity)),
          ),
      );
    },

    async removeCartPackage(id: identifier) {
      if (!this.cartDetails) return;
      this.cartDetails.cartPackages.splice(
        this.cartDetails.cartPackages.findIndex((i) => i.id == id),
        1,
      );
      await deleteCartPackage(this.persistedJobId as identifier, id);
    },

    async removePackage(id: identifier) {
      this.removingCartPackage = true;
      await this.removeCartPackage(id);
      if (!this.hasStandardPackage && this.cartDetails) {
        if (this.cartPackages) {
          await Promise.all(
            this.cartPackages!.map(async (p) => {
              await this.removeCartPackage(p.id);
            }),
          );
        }
        await this.deleteCart();
      } else if (this.hasAddonPackages) {
        await Promise.all(
          this.addonPackages!.map(async (p) => {
            if (!validatePackage(p, multiply(p.price, p.quantity), this.total, this.cartDetails?.cartPackages)) {
              await this.removeCartPackage(p.id);
            }
          }),
        );
      }
      this.removingCartPackage = false;
    },

    async deleteCart() {
      await deleteCart(this.persistedJobId as identifier);
      this.emptyCart();
    },
  },
});
